import type { ReactNode } from 'react'
import type { TopicResource } from '../../types/api'
import { GitHubConnectionPanels } from '../github/GitHubConnectionPanels'
import {
  CronBody,
  GenericJsonBody,
  GitHubBody,
  GitRepoBody,
  McpBody,
  NoteBody,
  PlanBody,
  QuestionBody,
  SqliteBody,
  TodoBody,
  WebBody,
  WebhookBody,
} from './ResourceViewBodies'

/**
 * Per-kind read-side view descriptor — the `/r/:id` body renderers,
 * symmetric to `resource-forms/` on the edit side. Adding a kind = add
 * an entry here; `ResourceDetailPage` needs no kind-specific knowledge.
 * Unknown kinds fall back to the generic JSON body, so nothing ever
 * renders empty.
 */
/** A resource viewed through the read-side renderers: the union's
 * kind-specific `data` variants collapse to an indexable bag, since
 * bodies read display fields without narrowing per kind. */
export type ViewResource = Omit<TopicResource, 'data'> & {
  data: Record<string, unknown>
}

/** Where a resource's events land and what event type to suggest for a
 * workflow — the "what happens to these events" card. */
interface EventHandlersTarget {
  topicId: string
  eventType: string
}

export interface ResourceViewDescriptor {
  /** Rich body block for the detail page. */
  Body: (props: { resource: ViewResource }) => ReactNode
  /** Kind-specific panels under the body (e.g. reconnecting an App). */
  Panels?: (props: { resource: ViewResource }) => ReactNode
  /** The topic this resource's events land in, if it delivers into one. */
  eventHandlers?: (resource: ViewResource) => EventHandlersTarget | null
}

/** A webhook delivers into its own topic. */
function webhookHandlers(r: ViewResource): EventHandlersTarget | null {
  if (!r.topic_id) return null
  const type = typeof r.data.event_type === 'string' ? r.data.event_type : ''
  return { topicId: r.topic_id, eventType: type || 'webhook.received' }
}

/** A route delivers into its target; suggest the event type it selects. */
function routeHandlers(r: ViewResource): EventHandlersTarget | null {
  const d = r.data
  if (d.target_topic_id == null) return null
  const first = Array.isArray(d.in) ? d.in[0] : d.equals
  return {
    topicId: String(d.target_topic_id),
    eventType:
      d.when === '{event_type}' && typeof first === 'string' ? first : '',
  }
}

const VIEW_REGISTRY: Record<string, ResourceViewDescriptor> = {
  note: { Body: NoteBody },
  plan: { Body: PlanBody },
  question: { Body: QuestionBody },
  todo_list: { Body: TodoBody },
  web: { Body: WebBody },
  mcp: { Body: McpBody },
  git_repo: { Body: GitRepoBody },
  github: { Body: GitHubBody, Panels: GitHubConnectionPanels },
  sqlite_db: { Body: SqliteBody },
  webhook: { Body: WebhookBody, eventHandlers: webhookHandlers },
  event_route: { Body: GenericJsonBody, eventHandlers: routeHandlers },
  cron: { Body: CronBody },
}

/** Never undefined: kinds without a hand-written view render the generic
 * JSON body over the full `data` blob. */
export function getResourceView(kind: string): ResourceViewDescriptor & {
  Body: (props: { resource: TopicResource }) => ReactNode
} {
  return VIEW_REGISTRY[kind] ?? { Body: GenericJsonBody }
}
